import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Plus, Trash2, Loader2, ScanLine } from 'lucide-react';
import { UploadInput } from '../../UploadInput';
import { SIM_NETWORKS, SIM_LOCATIONS } from '../../../utils/bankConstants';
import { extractOCR } from '../../../services/api';
import { toast } from 'sonner';

const OCRUpload = ({ label, documentType, onImageUploaded, onFieldsExtracted }) => {
  const [scanning, setScanning] = useState(false);

  const handleUpload = async (base64) => {
    onImageUploaded(base64);
    setScanning(true);
    try {
      const res = await extractOCR(base64, documentType);
      if (res.data.success && res.data.data) {
        onFieldsExtracted(res.data.data);
        toast.success(`${label} details extracted successfully`);
      } else {
        toast.info(`Could not auto-read ${label}. Please enter details manually.`);
      }
    } catch {
      toast.info(`OCR unavailable. Please enter ${label} details manually.`);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <Label className="flex items-center gap-1.5">
        <ScanLine className="w-3.5 h-3.5 text-blue-600" />Upload {label} {scanning && <Loader2 className="w-3 h-3 animate-spin text-blue-600" />}
      </Label>
      <UploadInput className="mt-1" onUpload={handleUpload} />
      {scanning && <p className="text-xs text-blue-600 mt-1">Scanning image for details...</p>}
    </div>
  );
};

const KycSimSection = ({ bankForm, updateForm, simInputs, onAddSIM, onRemoveSIM, onSIMChange }) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-4 p-4 bg-white border rounded-lg">
        <h3 className="font-semibold text-sm text-slate-700">KYC Details</h3>
        <div><Label>KYC Notes</Label><Textarea value={bankForm.kycNotes || ''} onChange={(e) => updateForm('kycNotes', e.target.value)} rows={2} /></div>

        <OCRUpload
          label="Aadhaar"
          documentType="aadhaar"
          onImageUploaded={(b64) => updateForm('aadhaarDataUrl', b64)}
          onFieldsExtracted={(data) => {
            if (data.aadhaarNumber) updateForm('aadhaarNo', data.aadhaarNumber);
            if (data.name) updateForm('holderName', data.name);
          }}
        />
        <div><Label>Aadhaar Number</Label><Input value={bankForm.aadhaarNo || ''} onChange={(e) => updateForm('aadhaarNo', e.target.value)} placeholder="Auto-filled from scan or enter manually" /></div>

        <OCRUpload
          label="PAN Card"
          documentType="pan"
          onImageUploaded={(b64) => updateForm('panDataUrl', b64)}
          onFieldsExtracted={(data) => {
            if (data.panNumber) updateForm('panNo', data.panNumber);
            if (data.name && !bankForm.holderName) updateForm('holderName', data.name);
          }}
        />
        <div><Label>PAN Number</Label><Input value={bankForm.panNo || ''} onChange={(e) => updateForm('panNo', e.target.value)} placeholder="Auto-filled from scan or enter manually" /></div>
      </div>
      <div className="space-y-4 p-4 bg-white border rounded-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm text-slate-700">SIM Details</h3>
          <Button type="button" onClick={onAddSIM} variant="outline" size="sm" className="gap-2"><Plus className="w-4 h-4" />Add SIM</Button>
        </div>
        <div>
          <Label>SIM Available</Label>
          <Select value={bankForm.simAvailable} onValueChange={(val) => updateForm('simAvailable', val)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {simInputs.map((sim, index) => (
            <div key={`sim-idx-${index}`} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg">
              <div className="flex-1 space-y-2">
                <Input placeholder="10-digit phone number" value={sim.number} onChange={(e) => onSIMChange(index, 'number', e.target.value)} maxLength={10} data-testid={`sim-number-${index}`} inputMode="numeric" />
                <Select value={sim.network} onValueChange={(val) => onSIMChange(index, 'network', val)}>
                  <SelectTrigger><SelectValue placeholder="Network" /></SelectTrigger>
                  <SelectContent>{SIM_NETWORKS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {simInputs.length > 1 && (
                <Button type="button" onClick={() => onRemoveSIM(index)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          ))}
        </div>
        <div>
          <Label>SIM Location</Label>
          <Select value={bankForm.simLocation || ''} onValueChange={(val) => updateForm('simLocation', val)}>
            <SelectTrigger data-testid="sim-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent>{SIM_LOCATIONS.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default KycSimSection;
